import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  createMeetingGroup,
  deleteMeetingGroup,
  getMeetingGroups,
  updateMeetingGroup,
} from '../api/meetingApi'
import type { MeetingGroupQuery, MeetingGroupUpsert } from '../types'

const KEY = 'meeting-groups'

/**
 * Toplantı grubu listesi (sunucu-taraflı sayfalama/arama) + mutasyonlar. `meta.handledCodes` DUPLICATE_NAME'i
 * global toast'tan muaf tutar (form alan hatası olarak gösterilir).
 */
export function useMeetingGroups(query: MeetingGroupQuery) {
  const qc = useQueryClient()
  const { i18n } = useTranslation()
  const invalidate = () => qc.invalidateQueries({ queryKey: [KEY] })

  // 122: kart ekranı (withMeetings) ile yönetim ekranı farklı küme döner; groupName dile göre çözülür.
  const list = useQuery({
    queryKey: [
      KEY,
      i18n.language,
      query.withMeetings ?? false,
      query.q ?? '',
      query.page ?? 0,
      query.size ?? 12,
    ],
    queryFn: () => getMeetingGroups(query),
  })

  const create = useMutation({
    mutationFn: (body: MeetingGroupUpsert) => createMeetingGroup(body),
    onSuccess: invalidate,
    meta: { handledCodes: ['DUPLICATE_NAME'] },
  })

  const update = useMutation({
    mutationFn: ({ id, body }: { id: number; body: MeetingGroupUpsert }) => updateMeetingGroup(id, body),
    onSuccess: invalidate,
    meta: { handledCodes: ['DUPLICATE_NAME'] },
  })

  const remove = useMutation({
    mutationFn: (id: number) => deleteMeetingGroup(id),
    onSuccess: invalidate,
    // Altında aktif toplantı olan grup silinemez (409); mesajı ekran gösterir → global toast'tan muaf.
    meta: { handledCodes: ['MEETING_GROUP_IN_USE'] },
  })

  return { list, create, update, remove }
}
